import React from "react";
import clsx from "clsx";
import styles from "./FeaturesSection.module.css";
import { Container } from "../../common/Container";
import { spotlightData, extrasData } from "../../../data/compassData";

const IMAGE_WIDTH = 828;

const icons: Record<string, React.ReactNode> = {
  bell: (
    <path d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
  ),
  clock: <path d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />,
  text: (
    <path d="M3.75 6.75h10.5M9 6.75v11.25M13.5 12.75h6.75M16.875 12.75V18" />
  ),
  cloud: (
    <path d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
  ),
};

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className={styles.pointIcon}>
    <path
      fillRule="evenodd"
      d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
      clipRule="evenodd"
    />
  </svg>
);

export const FeaturesSection: React.FC = () => (
  <section id="features" className={styles.section} aria-labelledby="features-title">
    <Container>
      <h2 id="features-title" className={styles.sectionTitle}>
        Inside Compass
      </h2>

      <div className={styles.spotlights}>
        {spotlightData.map((spot, index) => {
          const [top, bottom] = spot.crop;
          const cropHeight = Math.round(spot.imageHeight * (bottom - top));
          return (
            <article
              key={spot.id}
              id={spot.id}
              className={clsx(styles.spotlight, index % 2 === 1 && styles.spotlightFlipped)}
              aria-labelledby={`${spot.id}-title`}
            >
              <div className={styles.copy}>
                {spot.premium && <p className={styles.premiumTag}>Premium · 14 days free</p>}
                <h3 id={`${spot.id}-title`} className={styles.title}>
                  {spot.title}
                </h3>
                <p className={styles.description}>{spot.description}</p>
                <ul className={styles.points}>
                  {spot.points.map((point) => (
                    <li key={point}>
                      <CheckIcon />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <figure className={styles.media}>
                <div
                  className={styles.crop}
                  style={{ aspectRatio: `${IMAGE_WIDTH} / ${cropHeight}` }}
                >
                  <img
                    src={spot.image}
                    alt={spot.imageAlt}
                    width={IMAGE_WIDTH}
                    height={spot.imageHeight}
                    loading="lazy"
                    decoding="async"
                    style={{ transform: `translateY(-${(top * 100).toFixed(2)}%)` }}
                  />
                </div>
              </figure>
            </article>
          );
        })}
      </div>

      <div className={styles.extras}>
        <h3 className={styles.extrasTitle}>Also in Compass</h3>
        <ul className={styles.extrasList}>
          {extrasData.map((extra) => (
            <li key={extra.id} className={styles.extra}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className={styles.extraIcon}
              >
                {icons[extra.icon]}
              </svg>
              <div>
                <p className={styles.extraTitle}>{extra.title}</p>
                <p className={styles.extraDescription}>{extra.description}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Container>
  </section>
);
